import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { BottomNavigation } from 'components/common/bottomNavigation/BottomNavigation';
import { useMeetingList } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import './MeetingsList.css';

interface Meeting {
    id: string;
    title: string;
    host: string;
    status?: string;
    time?: string;
    date?: string;
    location?: string;
    dDay?: string;
    isRegularMeeting?: boolean;
    scheduleStartDateTime?: string;
}

export const MeetingsList: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const filter = searchParams.get('filter') || 'all';
    const [activeFilter, setActiveFilter] = useState(filter);
    const { data } = useMeetingList();

    // 컴포넌트 마운트 시 모임 목록 조회
    useEffect(() => {
        meetingStore.onGetMeetingList();
    }, []);

    // API 데이터를 Meeting 타입으로 변환 (홈 화면과 동일한 로직)
    const allMeetings: Meeting[] = data?.data?.map((meeting: any) => ({
        id: meeting.meetingId,
        title: meeting.meetingName,
        host: meeting.creatorNickname || '알 수 없음',
        status: meeting.status === 'ONGOING' ? '진행중' :
                meeting.status === 'COMPLETED' ? '종료' : '예정',
        time: meeting.schedule ?
            `${new Date(meeting.schedule.startDatetime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} - ${new Date(meeting.schedule.endDatetime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}` :
            '',
        date: meeting.schedule ?
            new Date(meeting.schedule.startDatetime).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit' }) :
            '',
        location: meeting.place?.placeAddress || meeting.place?.placeName || '',
        dDay: meeting.schedule ?
            (() => {
                const today = new Date();
                const meetingDate = new Date(meeting.schedule.startDatetime);
                const diffTime = meetingDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays > 0 ? `D-${diffDays}` : diffDays === 0 ? 'D-Day' : `D+${Math.abs(diffDays)}`;
            })() :
            undefined,
        isRegularMeeting: meeting.isRegularMeeting || false,
        scheduleStartDateTime: meeting.schedule?.startDatetime
    })) || [];

    // 날짜 기반으로 모임 필터링 (홈 화면과 동일한 로직)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const upcomingLimit = new Date(today);
    upcomingLimit.setDate(upcomingLimit.getDate() + 14); // 14일 이내

    // TODAY: 오늘 일정이 있는 모임
    const todayMeetings = allMeetings.filter(m => {
        if (!m.scheduleStartDateTime) return false;
        const meetingDate = new Date(m.scheduleStartDateTime);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate.getTime() === today.getTime();
    });

    // UPCOMING: 내일부터 14일 이내 일정이 있는 모임
    const upcomingMeetings = allMeetings.filter(m => {
        if (!m.scheduleStartDateTime) return false;
        const meetingDate = new Date(m.scheduleStartDateTime);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate >= tomorrow && meetingDate <= upcomingLimit;
    });

    // PARTICIPATING: 정기모임 또는 일정이 없거나 먼 미래인 모임
    const participatingMeetings = allMeetings.filter(m => {
        // 정기모임은 무조건 PARTICIPATING에 표시
        if (m.isRegularMeeting) return true;

        // 일정이 없는 모임
        if (!m.scheduleStartDateTime) return true;

        // 14일 이후 일정
        const meetingDate = new Date(m.scheduleStartDateTime);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate > upcomingLimit;
    });

    const getFilteredMeetings = () => {
        switch (activeFilter) {
            case 'today':
                return todayMeetings;
            case 'upcoming':
                return upcomingMeetings;
            case 'participating':
                return participatingMeetings;
            default:
                return allMeetings;
        }
    };

    const handleMeetingClick = (meetingId: string) => {
        navigate(`/meeting/detail/${meetingId}`);
    };

    return (
        <div className="meetings-list-wrapper">
            <div className="meetings-list-header">
                <button className="back-button" onClick={() => navigate('/home')}>
                    ←
                </button>
                <h1 className="meetings-list-title">모임 목록</h1>
            </div>

            <div className="meetings-list-content">
                {/* Filter Tabs */}
                <div className="filter-tabs">
                    <button
                        className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('all')}
                    >
                        전체
                    </button>
                    <button
                        className={`filter-tab ${activeFilter === 'today' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('today')}
                    >
                        오늘
                    </button>
                    <button
                        className={`filter-tab ${activeFilter === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('upcoming')}
                    >
                        예정
                    </button>
                    <button
                        className={`filter-tab ${activeFilter === 'participating' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('participating')}
                    >
                        참여중
                    </button>
                </div>

                {/* Meetings List */}
                <div className="meetings-list">
                    {getFilteredMeetings().map(meeting => (
                        <button
                            key={meeting.id}
                            className="meeting-list-item"
                            onClick={() => handleMeetingClick(meeting.id)}
                        >
                            <div className="meeting-list-info">
                                <div className="meeting-list-header-row">
                                    <h3 className="meeting-list-title">{meeting.title}</h3>
                                    {meeting.status && (
                                        <span className={`meeting-status ${meeting.status === '진행중' ? 'ongoing' : 'ended'}`}>
                                            {meeting.status}
                                        </span>
                                    )}
                                    {meeting.dDay && (
                                        <span className="meeting-dday">{meeting.dDay}</span>
                                    )}
                                </div>
                                <div className="meeting-list-meta">
                                    <span className="meeting-host">주최: {meeting.host}</span>
                                </div>
                                <div className="meeting-list-details">
                                    {meeting.time && <span>⏰ {meeting.time}</span>}
                                    {meeting.date && <span>📅 {meeting.date}</span>}
                                </div>
                                {meeting.location && (
                                    <div className="meeting-list-location">
                                        📍 {meeting.location}
                                    </div>
                                )}
                            </div>
                            <span className="meeting-list-arrow">›</span>
                        </button>
                    ))}
                </div>

                {getFilteredMeetings().length === 0 && (
                    <div className="empty-state">
                        <p>모임이 없습니다</p>
                    </div>
                )}
            </div>

            {/* Floating Action Button */}
            <button
                className="fab-create-meeting"
                onClick={() => navigate('/meeting/create/step1')}
                aria-label="모임 생성"
            >
                <span className="fab-icon">+</span>
            </button>

            <BottomNavigation />
        </div>
    );
};
